import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowRight, CalendarDays, Camera, Check, MapPin, PenLine } from 'lucide-react';
import {
  Alert,
  Button,
  Icon,
  KeyValueList,
  KeyValueRow,
  PhotoPicker,
  PhotoPickerHandle,
  ProductThumb,
  RadioCardGroup,
  StatusPill,
} from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import { formatIndianMobile } from '@/lib/support';
import type { ProductCase } from '@/types/product';
import type { ReturnDraft, ReturnOptions, ReturnResolution } from '@/types/returns';
import { comingSoon } from '@/utils/orderPresentation';
import { formatRupees } from '@/utils/productCases';
import { REQUIRED_PHOTOS, RETURN_REASONS, reasonTitle } from '@/utils/returnCases';

export type ReturnStepKey = 'reason' | 'photos' | 'resolution' | 'details' | 'confirm' | 'pickup';

export interface ReturnStepProps {
  order: ProductCase;
  options: ReturnOptions;
  draft: ReturnDraft;
  update: (patch: Partial<ReturnDraft>) => void;
  go: (step: ReturnStepKey) => void;
  /** Customer-facing order line, e.g. "EUME · ORD-EU48213 · ₹6,999 · Delivered 28 Oct". */
  orderLine: string;
}

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

const card = 'tw-flex tw-flex-col tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface';

const ContinueButton: React.FC<{ onClick: () => void; disabled?: boolean; children?: React.ReactNode }> = ({ onClick, disabled, children = 'Continue' }) => (
  <Button fullWidth trailingIcon={ArrowRight} onClick={onClick} disabled={disabled}>
    {children}
  </Button>
);

/** Product photo, name and order line (Figma "Product row"). */
export const ProductRow: React.FC<{ order: ProductCase; line: string; trailing?: React.ReactNode; size?: 'md' | 'sm' }> = ({ order, line, trailing, size = 'md' }) => (
  <div className="tw-flex tw-items-center tw-gap-[14px]">
    <ProductThumb src={order.imageUrl} alt={order.title} size={size} />
    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
      <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{order.title}</p>
      <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{line}</p>
    </div>
    {trailing}
  </div>
);

export const ProductCard: React.FC<{ order: ProductCase; line: string }> = ({ order, line }) => (
  <section aria-label="Item" className={`${card} tw-p-4`}>
    <ProductRow order={order} line={line} />
  </section>
);

/** R1 — why the item is going back. */
export const ReasonStep: React.FC<ReturnStepProps> = ({ order, draft, update, go, orderLine }) => (
  <>
    <ProductCard order={order} line={orderLine} />
    <RadioCardGroup label="Why are you returning it?" options={RETURN_REASONS} value={draft.reason} onChange={(reason) => update({ reason })} />
    <Alert title="Damaged by accident?">If it has a protection plan, a claim gets it repaired or replaced — no need to return it.</Alert>
    <ContinueButton onClick={() => go('photos')} disabled={!draft.reason} />
  </>
);

/** R2 / R2E — photos of the item, with the quality check's verdict. */
export const PhotosStep: React.FC<ReturnStepProps> = ({ order, options, draft, update, go }) => {
  const picker = useRef<PhotoPickerHandle>(null);
  const attempts = useRef<Record<string, number>>({});
  const merchant = order.merchant ?? 'The store';
  const flagged = draft.photos.find((p) => p.issue);
  const complete = REQUIRED_PHOTOS.every((label) => draft.photos.some((p) => p.label === label));

  const pick = (label: string, file: File) => {
    if (!/^image\/(jpeg|png)$/.test(file.type)) return toast.error('Please choose a JPG or PNG photo.');
    if (file.size > MAX_PHOTO_BYTES) return toast.error('Photos can be up to 10 MB each.');
    attempts.current[label] = (attempts.current[label] ?? 0) + 1;
    const photo = { label, url: URL.createObjectURL(file), issue: options.photoIssue?.(label, attempts.current[label]) };
    const replaced = REQUIRED_PHOTOS.includes(label) ? draft.photos.find((p) => p.label === label) : undefined;
    if (replaced) URL.revokeObjectURL(replaced.url);
    update({ photos: replaced ? draft.photos.map((p) => (p === replaced ? photo : p)) : [...draft.photos, photo] });
  };

  return (
    <>
      <section aria-labelledby="return-photos" className={`${card} tw-gap-3 tw-p-5`}>
        <h2 id="return-photos" className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
          Photos{draft.photos.length > 0 && ` · ${draft.photos.length} added`}
        </h2>
        <PhotoPicker ref={picker} required={REQUIRED_PHOTOS} photos={draft.photos} onPick={pick} />
        {!flagged && (
          <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">
            Add the front, back, and the tags or original box. For damaged items, add a close-up of the issue. JPG or PNG, up to 10 MB each.
          </p>
        )}
      </section>
      {flagged ? (
        <>
          <Alert tone="danger" title={`One photo is too ${flagged.issue}`}>
            Retake the {flagged.label.toLowerCase()} photo in good light so the whole item is visible.
          </Alert>
          <Button fullWidth leadingIcon={Camera} onClick={() => picker.current?.open(flagged.label)}>
            Retake photo
          </Button>
          <Button fullWidth disabled>
            Continue
          </Button>
        </>
      ) : (
        <>
          <Alert>Clear, well-lit photos help {merchant} approve returns faster.</Alert>
          <ContinueButton onClick={() => go('resolution')} disabled={!complete} />
        </>
      )}
    </>
  );
};

/** One-line description of each way to resolve the return. */
function resolutionOptions(order: ProductCase, options: ReturnOptions, only?: ReturnResolution[]) {
  const amount = formatRupees(order.price);
  const merchant = order.merchant ?? 'the store';
  const all: { value: ReturnResolution; title: string; description: string }[] = [
    { value: 'replacement', title: 'Replacement', description: `A new ${order.title}, at no extra cost` },
    { value: 'store_credit', title: `Store credit on ${merchant}`, description: `${amount} to use on your next ${merchant} order` },
    {
      value: 'refund',
      title: 'Refund to original payment',
      description: `${amount} back to ${options.refundMethod ?? 'your original payment method'} in 5–7 working days`,
    },
  ];
  return only ? all.filter((o) => only.includes(o.value)) : all;
}

/** R3 — replacement, store credit or refund. */
export const ResolutionStep: React.FC<ReturnStepProps> = ({ order, options, draft, update, go, orderLine }) => (
  <>
    <ProductCard order={order} line={orderLine} />
    <RadioCardGroup label="Resolution" options={resolutionOptions(order, options)} value={draft.resolution} onChange={(resolution) => update({ resolution })} />
    <ContinueButton onClick={() => go('details')} disabled={!draft.resolution} />
  </>
);

/** R4A / R4AE — pick the replacement piece, or another resolution when nothing's in stock. */
export const ReplacementStep: React.FC<ReturnStepProps> = ({ order, options, draft, update, go }) => {
  const inStock = options.variants.some((v) => v.inStock);
  const [instead, setInstead] = useState<ReturnResolution>();
  if (!inStock) {
    return (
      <>
        <Alert tone="warning" title="Replacement not available">
          {order.title} is out of stock in every colour right now. Pick another way to resolve your return.
        </Alert>
        <RadioCardGroup
          label="Choose instead"
          options={resolutionOptions(order, options, ['store_credit', 'refund'])}
          value={instead}
          onChange={setInstead}
        />
        {/* Switching the resolution re-renders step 4 as that resolution's details (R4B / R4C). */}
        <ContinueButton onClick={() => update({ resolution: instead })} disabled={!instead} />
      </>
    );
  }
  return (
    <>
      <RadioCardGroup
        label={options.variantGroup ?? order.title}
        options={options.variants.map((v) => ({
          value: v.id,
          title: v.current ? `${v.name} (same as yours)` : v.name,
          description: v.inStock ? 'In stock' : 'Out of stock',
          disabled: !v.inStock,
        }))}
        value={draft.variantId}
        onChange={(variantId) => update({ variantId })}
      />
      <Alert title="When it ships">Your replacement ships once the original is picked up. Track both here.</Alert>
      <ContinueButton onClick={() => go('confirm')} disabled={!draft.variantId} />
    </>
  );
};

/** Big amount with its details (Figma "Store credit" / "Refund amount" cards). */
const AmountCard: React.FC<{ label: string; amount: string; rows: KeyValueRow[] }> = ({ label, amount, rows }) => (
  <section aria-label={label} className={`${card} tw-gap-1 tw-p-5`}>
    <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{label}</p>
    <p className="tw-pb-2 tw-text-[28px] tw-font-bold tw-leading-9 tw-tracking-[-0.3px] tw-text-ds-text-primary">{amount}</p>
    <KeyValueList rows={rows} />
  </section>
);

/** R4B — store credit details. */
export const StoreCreditStep: React.FC<ReturnStepProps> = ({ order, options, go }) => (
  <>
    <AmountCard
      label="Store credit"
      amount={formatRupees(order.price)}
      rows={
        [
          options.storeCreditTo && { label: 'Credited to', value: options.storeCreditTo },
          { label: 'Available', value: 'After the item passes quality check' },
          options.storeDomain && { label: 'Use it on', value: `Your next order at ${options.storeDomain}` },
        ].filter(Boolean) as KeyValueRow[]
      }
    />
    <Alert tone="warning">Store credit can’t be changed to a refund later.</Alert>
    <ContinueButton onClick={() => go('confirm')} />
  </>
);

/** R4C — refund details. */
export const RefundStep: React.FC<ReturnStepProps> = ({ order, options, go }) => (
  <>
    <AmountCard
      label="Refund amount"
      amount={formatRupees(order.price)}
      rows={
        [
          options.refundTo && { label: 'Refund to', value: options.refundTo },
          { label: 'Timeline', value: '5–7 working days after quality check' },
        ].filter(Boolean) as KeyValueRow[]
      }
    />
    <Alert>You’ll get an SMS when the refund is initiated and again when it reaches your account.</Alert>
    <ContinueButton onClick={() => go('confirm')} />
  </>
);

/** A titled summary card with an Edit link (Figma R5 sections). */
const ReviewCard: React.FC<{ title: string; onEdit: () => void; children: React.ReactNode }> = ({ title, onEdit, children }) => (
  <section aria-label={title} className={`${card} tw-gap-3 tw-p-5`}>
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
      <h2 className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{title}</h2>
      <Button variant="link" leadingIcon={PenLine} onClick={onEdit} aria-label={`Edit ${title.toLowerCase()}`}>
        Edit
      </Button>
    </div>
    {children}
  </section>
);

/** Address, name and phone the pickup goes to: the return options' address, else the order's. */
function pickupContact(order: ProductCase, options: ReturnOptions, user: { name?: string | null; phone?: string | null } | null) {
  return {
    name: options.pickupAddress?.name ?? user?.name ?? undefined,
    phone: options.pickupAddress?.phone ?? formatIndianMobile(user?.phone),
    address: options.pickupAddress?.line ?? order.deliverTo,
  };
}

/** R5 / R5E — review everything before booking the pickup. */
export const ConfirmStep: React.FC<ReturnStepProps> = ({ order, options, draft, update, go }) => {
  const { user } = useAuth();
  const contact = pickupContact(order, options, user);
  const amount = formatRupees(order.price);
  const refundShort = options.refundMethod ? ` to ${options.refundMethod}` : '';
  const variant = options.variants.find((v) => v.id === draft.variantId);
  const plan = options.plan;
  const needsUnused = draft.reason !== 'damaged';

  const resolutionRows: KeyValueRow[] =
    draft.resolution === 'replacement'
      ? [
          { label: 'Replacement', value: [order.title, variant?.name].filter(Boolean).join(' · ') },
          { label: 'Cost to you', value: '₹0' },
        ]
      : draft.resolution === 'store_credit'
        ? [{ label: 'Store credit', value: amount }]
        : [{ label: 'Refund', value: `${amount}${refundShort}` }];
  if (plan && draft.resolution !== 'replacement') resolutionRows.push({ label: 'Plan refund', value: `${formatRupees(plan.fee)}${refundShort}` });

  return (
    <>
      <ReviewCard title="Item and reason" onEdit={() => go('reason')}>
        <ProductRow
          order={order}
          size="sm"
          line={[order.merchant, order.orderLabel, !plan && amount].filter(Boolean).join(' · ')}
          trailing={plan && <StatusPill tone="success">Protected</StatusPill>}
        />
        <KeyValueList plain rows={[{ label: 'Reason', value: reasonTitle(draft.reason) }]} />
      </ReviewCard>

      {draft.photos.length > 0 && (
        <ReviewCard title="Photos" onEdit={() => go('photos')}>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {draft.photos.map((p, i) => (
              <img key={i} src={p.url} alt={p.label} className="tw-h-14 tw-w-14 tw-rounded-xl tw-bg-ds-subtle tw-object-cover" />
            ))}
          </div>
        </ReviewCard>
      )}

      <ReviewCard title="Resolution" onEdit={() => go('resolution')}>
        <KeyValueList plain rows={resolutionRows} />
      </ReviewCard>

      <ReviewCard title="Contact and pickup address" onEdit={() => comingSoon('Changing the pickup address')}>
        <KeyValueList
          plain
          rows={
            [
              contact.name && { label: 'Name', value: contact.name },
              contact.phone && { label: 'Mobile', value: contact.phone },
              contact.address && { label: 'Address', value: contact.address },
            ].filter(Boolean) as KeyValueRow[]
          }
        />
      </ReviewCard>

      {plan &&
        (draft.resolution === 'replacement' ? (
          <Alert title="Your protection plan moves to the new piece">{plan.name} carries over to your replacement.</Alert>
        ) : (
          <Alert tone="warning" title="Your protection plan will be cancelled">
            {plan.name} on this item ends once the return is approved. The plan fee of {formatRupees(plan.fee)} is refunded in full. With a replacement, the plan
            moves to the new piece instead.
          </Alert>
        ))}

      {needsUnused && (
        <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-text-[14px] tw-leading-5 tw-text-ds-text-primary">
          <input
            type="checkbox"
            checked={draft.unusedConfirmed}
            onChange={(e) => update({ unusedConfirmed: e.target.checked })}
            className="tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-cursor-pointer tw-rounded tw-accent-[var(--ds-brand-primary)]"
          />
          The item is unused, with tags and original packaging.
        </label>
      )}

      <ContinueButton onClick={() => go('pickup')} disabled={needsUnused && !draft.unusedConfirmed}>
        Confirm and choose pickup
      </ContinueButton>
    </>
  );
};

/** R6 — pickup address and slot. */
export const PickupStep: React.FC<ReturnStepProps & { onSchedule: () => void; scheduling?: boolean }> = ({
  order,
  options,
  draft,
  update,
  onSchedule,
  scheduling,
}) => {
  const { user } = useAuth();
  const contact = pickupContact(order, options, user);
  return (
    <>
      <section aria-label="Pickup address" className={`${card} tw-flex-row tw-items-start tw-gap-3 tw-p-4`}>
        <Icon icon={MapPin} size={20} className="tw-mt-0.5 tw-text-ds-text-secondary" />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
          <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
            {[options.pickupAddress?.label, contact.name].filter(Boolean).join(' · ')}
          </p>
          <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{[contact.address, contact.phone].filter(Boolean).join(' · ')}</p>
        </div>
        <Button variant="link" onClick={() => comingSoon('Changing the pickup address')}>
          Change
        </Button>
      </section>

      {options.pickupSlots.length > 0 ? (
        <RadioCardGroup
          label="Pickup slot"
          options={options.pickupSlots.map((s) => ({ value: s.id, title: s.day, description: s.window }))}
          value={draft.slotId}
          onChange={(slotId) => update({ slotId })}
        />
      ) : (
        <Alert title="No pickup slots right now">Courier slots for your pincode open up daily. Please check again tomorrow.</Alert>
      )}

      <section aria-labelledby="pickup-checklist" className={`${card} tw-gap-2.5 tw-p-5`}>
        <h2 id="pickup-checklist" className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
          Before the courier arrives
        </h2>
        <ul className="tw-flex tw-flex-col tw-gap-2">
          {[
            'Pack the item in its original box, with tags on',
            options.returnId && `Keep the return ID ${options.returnId} handy`,
            'The courier checks the item before taking it',
          ]
            .filter(Boolean)
            .map((item) => (
              <li key={item as string} className="tw-flex tw-items-center tw-gap-2.5 tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                <Icon icon={Check} size={16} className="tw-text-ds-success-fg" />
                {item}
              </li>
            ))}
        </ul>
      </section>

      <Button fullWidth leadingIcon={CalendarDays} onClick={onSchedule} disabled={!draft.slotId} loading={scheduling}>
        Schedule pickup
      </Button>
    </>
  );
};
